import { useState, useEffect } from "react";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { FadeIn } from "@/hooks/useFadeIn";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [siteConfig, setSiteConfig] = useState({
    phone: "+45 12 34 56 78",
    email: "info@birk-anlaeg.dk",
    cvr: "12345678"
  });

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${API}/config`);
        setSiteConfig(res.data);
      } catch (err) {
        console.error("Error fetching config:", err);
      }
    };
    fetchConfig();
  }, []);

  // Format phone for tel: link (remove spaces)
  const phoneLink = `tel:${siteConfig.phone.replace(/\s+/g, '')}`;
  const emailLink = `mailto:${siteConfig.email}`;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Tak for din henvendelse! Vi vender tilbage hurtigst muligt.");
      setFormData({ name: "", phone: "", email: "", address: "", message: "" });
    } catch (err) {
      console.error("Error submitting form:", err);
      toast.error("Der opstod en fejl. Prøv venligst igen.");
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      label: "Telefon",
      value: siteConfig.phone,
      href: phoneLink,
      action: "Klik for at ringe",
    },
    {
      icon: Mail,
      label: "Email",
      value: siteConfig.email,
      href: emailLink,
      action: "Klik for at sende mail",
    },
    {
      icon: MapPin,
      label: "Område",
      value: "København og omegn",
      href: null,
      action: null,
    },
    {
      icon: Clock,
      label: "Svartid",
      value: "Inden for 24 timer",
      href: null,
      action: null,
    },
  ];

  return (
    <div data-testid="contact-page" className="pt-20">
      <SEO 
        title="Kontakt | Birk Anlæg"
        description="Kontakt Birk Anlæg for et uforpligtende tilbud på anlægsarbejde i København og omegn. Telefon: +45 51 92 54 29. Vi svarer inden for 24 timer."
        canonical="/kontakt"
      />
      {/* Hero */}
      <section className="bg-[#1A3C34] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1
            data-testid="contact-title"
            className="text-4xl md:text-6xl font-bold text-white mb-6"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Kontakt os
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Har du et projekt i tankerne? Kontakt os for et uforpligtende tilbud. 
            Vi svarer inden for 24 timer.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <FadeIn>
        <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Info & Map */}
            <div>
              <h2
                className="text-2xl md:text-3xl font-semibold text-[#1A3C34] mb-8"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Kontaktinformation
              </h2>

              <div className="space-y-6 mb-10">
                {contactInfo.map((item, index) => (
                  <div key={index} data-testid={`contact-info-${index}`}>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="flex items-start gap-4 p-4 bg-[#F9FAFB] rounded-xl hover:bg-gray-100 transition-colors group"
                      >
                        <div className="bg-[#1A3C34] p-3 rounded-lg text-white group-hover:bg-[#142F29] transition-colors">
                          <item.icon size={24} />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">{item.label}</p>
                          <p className="text-lg font-medium text-[#1A3C34]">
                            {item.value}
                          </p>
                          {item.action && (
                            <p className="text-sm text-[#1A3C34] mt-1">
                              {item.action}
                            </p>
                          )}
                        </div>
                      </a>
                    ) : (
                      <div className="flex items-start gap-4 p-4 bg-[#F9FAFB] rounded-xl">
                        <div className="bg-[#1A3C34] p-3 rounded-lg text-white">
                          <item.icon size={24} />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">{item.label}</p>
                          <p className="text-lg font-medium text-[#1A3C34]">
                            {item.value}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Google Maps */}
              <div className="mt-8">
                <h3
                  className="text-xl font-semibold text-[#1A3C34] mb-4"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  Find os
                </h3>
                <div className="aspect-video rounded-xl overflow-hidden bg-gray-100">
                  <iframe
                    data-testid="google-map"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d71997.25846068529!2d12.4698!3d55.6761!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4652533c5c803d23%3A0x4dd7edde69467b8!2sK%C3%B8benhavn!5e0!3m2!1sda!2sdk!4v1699999999999!5m2!1sda!2sdk"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Birk Anlæg lokation"
                  />
                </div>
                <a
                  href="https://www.google.com/maps/dir//K%C3%B8benhavn"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="directions-link"
                  className="inline-flex items-center gap-2 text-[#1A3C34] font-medium mt-4 hover:text-[#142F29] transition-colors"
                >
                  <MapPin size={18} />
                  Få rutevejledning
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2
                className="text-2xl md:text-3xl font-semibold text-[#1A3C34] mb-8"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Send en besked
              </h2>

              {submitted ? (
                <div
                  data-testid="form-success"
                  className="bg-green-50 border border-green-200 rounded-xl p-8 text-center"
                >
                  <CheckCircle size={48} className="mx-auto text-green-600 mb-4" />
                  <h3 className="text-xl font-semibold text-green-800 mb-2">
                    Tak for din henvendelse!
                  </h3>
                  <p className="text-green-700">
                    Vi har modtaget din besked og vender tilbage inden for 24 timer.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-6 text-[#1A3C34] font-medium hover:text-[#142F29] transition-colors"
                  >
                    Send en ny besked
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-testid="contact-form"
                  className="space-y-6"
                >
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Navn *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      data-testid="input-name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 focus:border-[#1A3C34] focus:ring-1 focus:ring-[#1A3C34] outline-none transition-colors"
                      placeholder="Dit fulde navn"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Telefon *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        data-testid="input-phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 focus:border-[#1A3C34] focus:ring-1 focus:ring-[#1A3C34] outline-none transition-colors"
                        placeholder="Dit telefonnummer"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        data-testid="input-email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 focus:border-[#1A3C34] focus:ring-1 focus:ring-[#1A3C34] outline-none transition-colors"
                        placeholder="Din email adresse"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="address"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Adresse / Område
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      data-testid="input-address"
                      value={formData.address}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 focus:border-[#1A3C34] focus:ring-1 focus:ring-[#1A3C34] outline-none transition-colors"
                      placeholder="F.eks. Frederiksberg, 2000"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Besked *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      data-testid="input-message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 focus:border-[#1A3C34] focus:ring-1 focus:ring-[#1A3C34] outline-none transition-colors resize-none"
                      placeholder="Beskriv dit projekt eller spørgsmål..."
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="submit-button"
                    disabled={loading}
                    className="w-full bg-[#1A3C34] text-white hover:bg-[#142F29] rounded-full px-8 py-4 text-lg font-medium transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sender...
                      </>
                    ) : (
                      <>
                        <Send size={20} />
                        Send besked
                      </>
                    )}
                  </button>

                  <p className="text-sm text-gray-500 text-center">
                    Vi svarer normalt inden for 24 timer på hverdage.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
      </FadeIn>
    </div>
  );
};

export default ContactPage;
